import { useCallback, useRef, useState, type MutableRefObject } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import type { ContainerAction } from '@/lib/containerActions';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import type { Container } from '@/types/container';

const COPY: Record<ContainerAction, { title: string; label: string; busy: string; description: (n: string) => string; success: string; failure: string }> = {
  start: { title: 'Start container?', label: 'Start', busy: 'Starting…', description: (n) => `Start '${n}'?`, success: 'Container started', failure: 'Failed to start container' },
  stop: { title: 'Stop container?', label: 'Stop', busy: 'Stopping…', description: (n) => `Stop '${n}'? Its processes will be stopped.`, success: 'Container stopped', failure: 'Failed to stop container' },
  restart: { title: 'Restart container?', label: 'Restart', busy: 'Restarting…', description: (n) => `Restart '${n}'? It will be briefly unavailable.`, success: 'Container restarted', failure: 'Failed to restart container' },
};

const CALLS: Record<ContainerAction, (id: number) => Promise<void>> = {
  start: (id) => api.containers.start(id),
  stop: (id) => api.containers.stop(id),
  restart: (id) => api.containers.restart(id),
};

export interface ContainerActionDialogProps {
  container: Container;
  /** The action being confirmed; null keeps the dialog closed. */
  action: ContainerAction | null;
  onClose: () => void;
  /** Called after the request settles (success or failure) so the page can refetch. */
  onChanged: () => void;
}

/**
 * The shell is always mounted; the body mounts only while an action is pending, so submitting and error
 * state reset on every open. Closing is blocked while the request is in flight, and a failure is shown
 * inside the dialog (and focused) instead of a toast.
 */
export function ContainerActionDialog({ container, action, onClose, onChanged }: ContainerActionDialogProps) {
  const submittingRef = useRef(false);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  return (
    <Dialog
      open={action !== null}
      onOpenChange={(next) => {
        if (!next && !submittingRef.current) onClose();
      }}
    >
      {action !== null ? (
        <ActionBody
          key={`${container.id}:${action}`}
          container={container}
          action={action}
          onClose={onClose}
          onChanged={onChanged}
          submittingRef={submittingRef}
          captureOpener={captureOpener}
          restoreFocus={restoreFocus}
        />
      ) : null}
    </Dialog>
  );
}

interface BodyProps {
  container: Container;
  action: ContainerAction;
  onClose: () => void;
  onChanged: () => void;
  submittingRef: MutableRefObject<boolean>;
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}

function ActionBody({ container, action, onClose, onChanged, submittingRef, captureOpener, restoreFocus }: BodyProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = COPY[action];
  const destructive = action === 'stop';

  // Stable callback ref: focuses the alert when it mounts, never on unrelated re-renders.
  const alertRef = useCallback((el: HTMLParagraphElement | null) => {
    el?.focus();
  }, []);

  const submit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    let ok = false;
    try {
      await CALLS[action](container.id);
      ok = true;
      toast.success(copy.success);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : copy.failure);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
    onChanged(); // state may have changed even on failure
    if (ok) onClose();
  };

  const block = (e: Event) => {
    if (submittingRef.current) e.preventDefault();
  };

  return (
    <DialogContent
      className="sm:max-w-md"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        cancelRef.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={block}
      onPointerDownOutside={block}
      onInteractOutside={block}
    >
      <DialogHeader>
        <DialogTitle>{copy.title}</DialogTitle>
        <DialogDescription className="[overflow-wrap:anywhere]">{copy.description(container.name)}</DialogDescription>
      </DialogHeader>
      {error ? (
        <p ref={alertRef} tabIndex={-1} role="alert" className="text-sm text-destructive outline-none">
          {error}
        </p>
      ) : null}
      <DialogFooter className="gap-2 sm:gap-0">
        <Button ref={cancelRef} type="button" variant="outline" disabled={submitting} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant={destructive ? 'destructive' : 'default'}
          disabled={submitting}
          aria-busy={submitting}
          onClick={() => void submit()}
        >
          {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
          {submitting ? copy.busy : error ? 'Retry' : copy.label}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
